import {
  Button,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  DatePicker,
  Upload,
  message,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import SmsApi, { Advertise } from 'apis/sms';
import commonApi from 'apis/common';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { typeOptionList } from './ad-list';
import 'react-quill/dist/quill.snow.css';
import dayjs, { Dayjs } from 'dayjs';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { CMS_LANGUAGE_OPTION_LIST } from 'constant/language';
const { RangePicker } = DatePicker;

type FormAdvertise = Advertise & { time: [Dayjs, Dayjs] };

// 自定义广告图片上传组件（使用 uploadProductImage 接口）
const AdImageUpload = ({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (value: string) => void;
}) => {
  // 自定义上传方法
  const customRequest = async (options: UploadRequestOption) => {
    const { file, onSuccess, onError } = options;

    try {
      // 检查 file 是否是 File 类型
      if (!(file instanceof File)) {
        message.error('上传失败：文件格式不正确');
        onError?.(new Error('文件格式不正确'));
        return;
      }

      const response = await commonApi.uploadProductImage(file, {
        quality: 100,
      });
      const url = response.url;

      if (!url) {
        message.error('上传失败：未返回图片URL');
        onError?.(new Error('未返回图片URL'));
        return;
      }

      // 调用 onSuccess 触发 Upload 组件的 onChange
      onSuccess?.(response);

      onChange?.(url);
    } catch (error) {
      console.error('上传失败:', error);
      message.error('图片上传失败');
      onError?.(error as Error);
    }
  };

  return (
    <div className="flex gap-4">
      {value && (
        <div className="relative">
          <img
            src={value}
            alt=""
            className="w-36 h-36 object-cover border border-dashed"
          />
          <DeleteOutlined
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 cursor-pointer"
            onClick={() => onChange?.('')}
          />
        </div>
      )}
      {!value && (
        <Upload
          className="w-36 h-36 flex justify-center items-center border border-dashed rounded cursor-pointer"
          customRequest={customRequest}
          showUploadList={false}
          accept="image/*"
        >
          <PlusOutlined className="w-36 h-36 flex justify-center items-center" />
        </Upload>
      )}
    </div>
  );
};

const ADDetailPage = () => {
  const [searchParams] = useSearchParams();
  const [detail, setDetail] = useState<Advertise>();
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    setLoading(true);
    SmsApi.getAdDetail(Number(id))
      .then((data) => {
        const { endTime, startTime, ...rest } = data;
        const t: FormAdvertise = {
          ...rest,
          endTime,
          startTime,
          time: [dayjs(startTime), dayjs(endTime)],
        };
        setDetail(data);
        form.setFieldsValue(t);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [form, searchParams]);

  return (
    <div className="max-w-4xl flex justify-center mx-auto">
      <Form
        layout="vertical"
        form={form}
        className="w-full"
        onFinish={async ({ time, ...rest }) => {
          form.validateFields().then(async (d) => {
            setLoading(true);
            const payload = {
              ...rest,
              startTime: dayjs(time[0]),
              endTime: dayjs(time[1]),
            };
            if (detail) {
              await SmsApi.AdEditService({
                id: detail.id,
                ...payload,
              });
              window.location.reload();
            } else {
              const { id } = await SmsApi.AdEditService({ ...payload });
              window.location.href = `/sms/ad-detail?id=${id}`;
            }
          });
        }}
        initialValues={{
          type: 0,
          language: 'all',
        }}
      >
        <Form.Item
          name="name"
          label={i18n.t('ad_name')}
          required
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="type" label={i18n.t('ad_placement')}>
          <Select
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={typeOptionList}
          />
        </Form.Item>
        <Form.Item
          name="time"
          label={`${i18n.t('start_time')}/${i18n.t('end_time')}`}
          required
          rules={[
            {
              required: true,
            },
          ]}
        >
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            className="w-full"
          />
        </Form.Item>
        <Form.Item
          name="status"
          label={i18n.t('online_offline')}
          getValueProps={(value) => {
            return {
              value: !!value,
            };
          }}
          normalize={(value) => {
            return value ? 1 : 0;
          }}
        >
          <Switch />
        </Form.Item>
        <Form.Item name="language" label={i18n.t('language')}>
          <Radio.Group>
            {CMS_LANGUAGE_OPTION_LIST.map(({ value, label }) => {
              return (
                <Radio key={value} value={value}>
                  {label}
                </Radio>
              );
            })}
          </Radio.Group>
        </Form.Item>
        <Form.Item name="pic" label={i18n.t('ad_image')}>
          <AdImageUpload />
        </Form.Item>
        <Form.Item name="sort" label={i18n.t('sort')}>
          <InputNumber className="w-full" />
        </Form.Item>
        <Form.Item
          name="url"
          label={i18n.t('ad_link')}
          required
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input className="w-full" />
        </Form.Item>
        <Form.Item name="note" label={i18n.t('remark')}>
          <Input.TextArea rows={5}></Input.TextArea>
        </Form.Item>
        <Form.Item className="mt-20 flex justify-center">
          <Button type="primary" htmlType="submit" loading={loading}>
            {i18n.t('confirm')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ADDetailPage;
